import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { styles } from "../../styles/KidsSchoolLicenseModal.styles"
import { useTranslation } from "react-i18next"
import CloseIcon from "@mui/icons-material/Close"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import LockIcon from "@mui/icons-material/Lock"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import CloudDoneIcon from "@mui/icons-material/CloudDone"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import SendIcon from "@mui/icons-material/Send"
import ShieldIcon from "@mui/icons-material/VerifiedUserOutlined"
import { useAuthContext } from "../../../../contexts/AuthContext"

const smashyImg = require("./1 BM.png")
const smashyorImg = require("./2 BM.png")

export default function KidsSchoolLicenseModal({
  open,
  onClose,
  onContactTeacher,
}) {
  const { t } = useTranslation()
  const { accountType } = useAuthContext()
  const titleText = t("your_access_is_pending", "Your Access Is Pending")
  const titleWords = titleText.trim().split(/\s+/)
  const midPoint = Math.ceil(titleWords.length / 2)
  const firstPart = titleWords.slice(0, midPoint).join(" ")
  const secondPart = titleWords.slice(midPoint).join(" ")

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: styles.dialog,
      }}
    >
      <IconButton onClick={onClose} sx={styles.closeButton}>
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Box sx={styles.container}>
        <Box sx={styles.badgeContainer}>
          <AccessTimeIcon sx={styles.badgeIcon} />
          <Stack direction="column" spacing={0.25}>
            <Typography sx={styles.badgeLabel}>
              {t("license_status", "License Status")}
            </Typography>
            <Typography sx={styles.badgeValue}>
              {t("awaiting_school_activation", "Awaiting School Activation")}
            </Typography>
          </Stack>
        </Box>
        <Box sx={styles.headerRow}>
          <Box sx={styles.headerLeft}>
            <Box sx={styles.lockTitleRow}>
              <LockIcon sx={styles.lockIcon} />
              <Typography component="h1" sx={styles.titlePending}>
                {firstPart}
              </Typography>
            </Box>
            <Typography component="h1" sx={styles.titlePending2}>
              {secondPart}
            </Typography>
            <Typography sx={styles.descriptionText}>
              {t(
                "your_teacher_needs_to_activate_licence",
                "Your teacher or school needs to activate your student license before you can continue this assignment."
              )}
            </Typography>

            <Box sx={styles.savedProgressRow}>
              <ShieldIcon sx={styles.savedProgressIcon} />
              <Typography sx={styles.savedProgressText}>
                {t(
                  "your_completed_work_saved",
                  "Your completed work and progress have already been saved."
                )}
              </Typography>
            </Box>
          </Box>
          <Box sx={styles.mascotContainer}>
            <Box
              component="img"
              src={accountType < 7 ? smashyImg : smashyorImg}
              alt="Smashy character"
              sx={styles.mascot}
            />
          </Box>
        </Box>
        <Box sx={styles.sectionDividerRow}>
          <Box sx={styles.dividerLine} />
          <Typography sx={styles.sectionLabel}>
            {t("heres_whats_happening", "Here's what's happening")}
          </Typography>
          <Box sx={styles.dividerLine} />
        </Box>
        <Box sx={styles.cardsContainer}>
          <Box
            sx={[
              styles.card,
              (theme) => ({ backgroundColor: theme.palette.licenses.surface3 }),
            ]}
          >
            <Box
              sx={[
                styles.cardIconWrapper,
                (theme) => ({
                  backgroundColor: theme.palette.licenses.surface11,
                }),
              ]}
            >
              <LockOutlinedIcon sx={styles.cardIcon} />
            </Box>
            <Stack>
              <Typography sx={styles.cardTitle}>
                {t("license_activation_pending", "License activation pending")}
              </Typography>
              <Typography sx={styles.cardDesc}>
                {t(
                  "your_school_has_not_activated_license",
                  "Your school has not activated this student license yet."
                )}
              </Typography>
            </Stack>
          </Box>
          <Box
            sx={[
              styles.card,
              (theme) => ({ backgroundColor: theme.palette.licenses.surface4 }),
            ]}
          >
            <Box
              sx={[
                styles.cardIconWrapper,
                (theme) => ({
                  backgroundColor: theme.palette.licenses.surface12,
                }),
              ]}
            >
              <CloudDoneIcon sx={styles.cardIcon} />
            </Box>
            <Stack>
              <Typography sx={styles.cardTitle}>
                {t("progress_safely_saved", "Progress safely saved")}
              </Typography>
              <Typography sx={styles.cardDesc}>
                {t(
                  "your_completed_work_remains",
                  "Your completed work and scores remain available once your access is activated."
                )}
              </Typography>
            </Stack>
          </Box>
          <Box
            sx={[
              styles.card,
              (theme) => ({ backgroundColor: theme.palette.licenses.surface5 }),
            ]}
          >
            <Box
              sx={[
                styles.cardIconWrapper,
                (theme) => ({
                  backgroundColor: theme.palette.licenses.surface13,
                }),
              ]}
            >
              <CheckCircleIcon sx={styles.cardIcon} />
            </Box>
            <Stack>
              <Typography sx={styles.cardTitle}>
                {t("continue_instantly", "Continue instantly")}
              </Typography>
              <Typography sx={styles.cardDesc}>
                {t(
                  "once_activated_you_can_continue",
                  "Once activated, you can continue right where you left off."
                )}
              </Typography>
            </Stack>
          </Box>
        </Box>
        <Box sx={styles.bannerContainer}>
          <Box sx={styles.bannerLeft}>
            <Box sx={styles.bannerIconWrapper}>
              <MailOutlineIcon sx={styles.bannerIcon} />
            </Box>
            <Box sx={styles.bannerText}>
              <Typography sx={styles.bannerTitle}>
                {t("need_access_urgently", "Need access urgently?")}
              </Typography>
              <Typography sx={styles.bannerDesc}>
                {t(
                  "you_can_send_a_reminder_teacher",
                  "You can send a reminder to your teacher or school administrator."
                )}
              </Typography>
            </Box>
          </Box>
          <Button
            disableElevation
            variant="outlined"
            onClick={onContactTeacher}
            startIcon={<SendIcon />}
            sx={styles.bannerButton}
          >
            {t("send_reminder", "Send Reminder")}
          </Button>
        </Box>
        <Box sx={styles.actionsRow}>
          <Button
            disableElevation
            variant="outlined"
            onClick={onClose}
            sx={styles.btnLater}
          >
            {t("continue_later", "Continue Later")}
          </Button>

          <Box sx={styles.dividerOr}>
            <Box sx={styles.dividerOrLine} />
            <Typography sx={styles.dividerOrText}>{t("or")}</Typography>
            <Box sx={styles.dividerOrLine} />
          </Box>

          <Button
            disableElevation
            variant="contained"
            onClick={onContactTeacher}
            startIcon={<SendIcon />}
            sx={styles.btnReminder}
          >
            {t("send_reminder", "Send Reminder")}
          </Button>
        </Box>
        <Box sx={styles.footerRow}>
          <LockIcon sx={styles.footerIcon} />
          <Typography sx={styles.footerText}>
            {t(
              "your_progress_is_safe",
              "Your progress is safe and nothing will be lost."
            )}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  )
}
